/**
 * Pure logic for the Entity Drilldown vertical stage list.
 *
 * Given a part's stored derived state (currentStageNumber, isReadyForPartStages,
 * partialBatchIndicator) plus its recorded part-stage-dates and inspection
 * events, plus the project's BG status, derive — per stage 1 through 18 —
 * what the drilldown should show:
 *
 *   - status icon (completed / current / locked / skipped / pending)
 *   - the recorded date (if any) or outcome (for 9/11/13)
 *   - whether the row is editable in the current project state
 *   - the inline reason if it isn't (so the user understands "what unlocks this?")
 *
 * The 18 stages are grouped into five clusters per the UX doc:
 *   Procurement (1-5), Preparation (6-8), Inspection (9-11),
 *   Delivery (12-13), Billing (14-18).
 *
 * Pass 20 made `currentStageNumber` honest — it now reflects the highest
 * stage with a closed fact. So a stage is "completed" if stageNumber ≤
 * currentStageNumber, "current" if it equals currentStageNumber + 1 and is
 * editable, etc. The cluster boundaries, skip semantics for stage 10/11 when
 * stage 9 is ACCEPTED, and the invoice-gate dependency for stage 16 are
 * encoded here so the page component stays declarative.
 */

import type {
  InspectionEvent,
  InspectionOutcome,
  PartStageDate,
  PartStageNumber,
  ProjectItem,
} from './pipeline-queries.js';
import type { BankGuarantee } from './pipeline-queries.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export const STAGE_NUMBERS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18,
] as const;
export type StageNumber = (typeof STAGE_NUMBERS)[number];

export type StageCluster = 'PROCUREMENT' | 'PREPARATION' | 'INSPECTION' | 'DELIVERY' | 'BILLING';

export type StageStatus =
  /** Stage is complete — a date/outcome closes it. */
  | 'completed'
  /** Stage is the one currently "active" (next to complete). Editable. */
  | 'current'
  /** Stage is in the future but not yet unlocked. Read-only. */
  | 'locked'
  /** Stage doesn't apply on this path (e.g. 10/11 after stage 9 ACCEPTED). */
  | 'skipped'
  /** Stage is part-level but the part has no batches / sub-parts to anchor it. */
  | 'not-applicable';

/** Per-row representation that the drilldown component renders. */
export interface StageRowState {
  stageNumber: StageNumber;
  label: string;
  cluster: StageCluster;
  status: StageStatus;
  /** Recorded date (for stages with a part_stage_date row, or inspection.eventDate). */
  date: string | null;
  /** Inspection outcome (for 9, 11, 13 only). */
  outcome: InspectionOutcome | null;
  /** Resolved date for non-ACCEPTED inspection outcomes. */
  resolvedDate: string | null;
  /** Whether the user can record / edit this stage now. */
  editable: boolean;
  /** When not editable, why — for inline display. */
  lockReason: string | null;
  /** Stage 9/11/13 (inspection events) render via the drawer, not inline. */
  isInspectionEvent: boolean;
  /** Stage 6 / 14 highlight when the aggregate gate / billing chain unlocks. */
  isClusterAnchor: boolean;
}

// ---------------------------------------------------------------------------
// Static metadata per stage
// ---------------------------------------------------------------------------

interface StageMeta {
  label: string;
  cluster: StageCluster;
  isInspectionEvent: boolean;
  isClusterAnchor: boolean;
}

export const STAGE_META: Record<StageNumber, StageMeta> = {
  1: { label: 'Order placed', cluster: 'PROCUREMENT', isInspectionEvent: false, isClusterAnchor: true },
  2: { label: 'Pre-shipment inspection', cluster: 'PROCUREMENT', isInspectionEvent: false, isClusterAnchor: false },
  3: { label: 'In transit', cluster: 'PROCUREMENT', isInspectionEvent: false, isClusterAnchor: false },
  4: { label: 'Arrived', cluster: 'PROCUREMENT', isInspectionEvent: false, isClusterAnchor: false },
  5: { label: 'In-house inspection', cluster: 'PROCUREMENT', isInspectionEvent: false, isClusterAnchor: false },
  6: { label: 'Assembling', cluster: 'PREPARATION', isInspectionEvent: false, isClusterAnchor: true },
  7: { label: 'Markings / casing', cluster: 'PREPARATION', isInspectionEvent: false, isClusterAnchor: false },
  8: { label: 'Inspection offered', cluster: 'PREPARATION', isInspectionEvent: false, isClusterAnchor: false },
  9: { label: 'Inspection (CMI / IRR)', cluster: 'INSPECTION', isInspectionEvent: true, isClusterAnchor: true },
  10: { label: 'Action plan', cluster: 'INSPECTION', isInspectionEvent: false, isClusterAnchor: false },
  11: { label: 'Re-inspection', cluster: 'INSPECTION', isInspectionEvent: true, isClusterAnchor: false },
  12: { label: 'Delivered', cluster: 'DELIVERY', isInspectionEvent: false, isClusterAnchor: true },
  13: { label: 'Functional check', cluster: 'DELIVERY', isInspectionEvent: true, isClusterAnchor: false },
  14: { label: 'I-Note received', cluster: 'BILLING', isInspectionEvent: false, isClusterAnchor: true },
  15: { label: 'CRV issued', cluster: 'BILLING', isInspectionEvent: false, isClusterAnchor: false },
  16: { label: 'Invoice submitted', cluster: 'BILLING', isInspectionEvent: false, isClusterAnchor: false },
  17: { label: 'Cheque released', cluster: 'BILLING', isInspectionEvent: false, isClusterAnchor: false },
  18: { label: 'Cheque cleared', cluster: 'BILLING', isInspectionEvent: false, isClusterAnchor: false },
};

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export interface PipelineStateInputs {
  /** The part itself — derived state (currentStageNumber, isReadyForPartStages). */
  item: Pick<
    ProjectItem,
    | 'id'
    | 'itemType'
    | 'currentStageNumber'
    | 'isReadyForPartStages'
    | 'partialBatchIndicator'
    | 'hasSubParts'
    | 'activeOrderedQuantity'
    | 'totalRequiredQuantity'
  >;
  partStageDates: readonly PartStageDate[];
  inspectionEvents: readonly InspectionEvent[];
  /** Whether the project has at least one ACCEPTED BG (invoice gate). */
  hasAcceptedBg: boolean;
  /** Whether the project is closed (locks everything for non-Admin users). */
  isProjectClosed: boolean;
  /**
   * Whether the current user is an Admin. Per spec §9, Admin retains edit
   * access on closed projects; Assistants are locked out. Defaults to
   * false for safety — pass `true` explicitly when the user is Admin.
   */
  userIsAdmin?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStageDate(
  partStageDates: readonly PartStageDate[],
  stage: PartStageNumber,
): string | null {
  return partStageDates.find((s) => s.stageNumber === stage)?.completedDate ?? null;
}

function getLatestInspection(
  events: readonly InspectionEvent[],
  stage: 9 | 11 | 13,
): InspectionEvent | null {
  const matches = events.filter((e) => e.stageNumber === stage);
  if (matches.length === 0) return null;
  return matches.reduce((acc, e) => (e.eventRound > acc.eventRound ? e : acc), matches[0]!);
}

/** Is the latest inspection event for `stage` "closed" (advance-permitting)? */
function isInspectionClosed(events: readonly InspectionEvent[], stage: 9 | 11 | 13): boolean {
  const latest = getLatestInspection(events, stage);
  if (!latest) return false;
  if (latest.outcome === 'ACCEPTED') return true;
  // Stage 13 has a UAT-specific re-functional-check rule: a failed
  // Functional Check with a resolved date unlocks round 2, but billing is
  // still blocked until the latest stage-13 round is ACCEPTED.
  if (stage === 13) return false;
  return latest.resolvedDate !== null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compute the rendered state for every stage on this part, in stage order.
 *
 * Skip semantics:
 *   - Parts without sub-parts skip stage 6 (Assembling) and move from
 *     aggregate-gate readiness directly to stage 7.
 *   - When stage 9 outcome is ACCEPTED, stages 10 and 11 become 'skipped'.
 *   - Sub-parts have no part-level stages (6-18 are 'not-applicable').
 */
export function computePipelineState(inputs: PipelineStateInputs): StageRowState[] {
  const {
    item,
    partStageDates,
    inspectionEvents,
    hasAcceptedBg,
    isProjectClosed: rawProjectClosed,
    userIsAdmin = false,
  } = inputs;
  // Spec §9 — closed projects lock edits against Assistants only; Admin
  // retains edit access. Combine both signals once at the entry point so
  // every builder downstream can treat `isProjectClosed` as the effective
  // "should lock for this user" flag.
  const isProjectClosed = rawProjectClosed && !userIsAdmin;
  const current = item.currentStageNumber;

  // Sub-parts have stages 1-5 (via batches) only. Stages 6-18 are not relevant
  // at the sub-part level.
  if (item.itemType === 'SUB_PART') {
    return STAGE_NUMBERS.map((n) =>
      buildSubPartRow(n, current, isProjectClosed, item.partialBatchIndicator),
    );
  }

  // PART path
  const s9Latest = getLatestInspection(inspectionEvents, 9);
  const s9Outcome = s9Latest?.outcome ?? null;
  const s9Closed = isInspectionClosed(inspectionEvents, 9);
  const s11Closed = isInspectionClosed(inspectionEvents, 11);
  const s13Closed = isInspectionClosed(inspectionEvents, 13);

  return STAGE_NUMBERS.map((stage) => {
    const meta = STAGE_META[stage];

    // Stages 1-5 — driven by batches, not part-stages. Reported as
    // completed/current based on currentStageNumber, with the partial-batch
    // indicator surfaced as a small hint on stage 5 when applicable.
    if (stage <= 5) {
      return buildBatchStageRow(
        stage as 1 | 2 | 3 | 4 | 5,
        current,
        item.partialBatchIndicator,
        isProjectClosed,
      );
    }

    // Stage 6 — Assembling only applies when a part has sub-parts.
    if (stage === 6 && !item.hasSubParts) {
      return {
        stageNumber: stage,
        label: meta.label,
        cluster: meta.cluster,
        status: 'skipped',
        date: null,
        outcome: null,
        resolvedDate: null,
        editable: false,
        lockReason: 'Skipped — parts without sub-parts do not require Assembling',
        isInspectionEvent: meta.isInspectionEvent,
        isClusterAnchor: meta.isClusterAnchor,
      };
    }

    // Stages 10/11 — auto-skipped when stage 9 is ACCEPTED
    if ((stage === 10 || stage === 11) && s9Outcome === 'ACCEPTED') {
      return {
        stageNumber: stage,
        label: meta.label,
        cluster: meta.cluster,
        status: 'skipped',
        date: null,
        outcome: null,
        resolvedDate: null,
        editable: false,
        lockReason: 'Auto-skipped — stage 9 outcome was ACCEPTED',
        isInspectionEvent: meta.isInspectionEvent,
        isClusterAnchor: meta.isClusterAnchor,
      };
    }

    // Inspection events (9, 11, 13). Stage 13 may have round 2 as
    // Re-functional Check when round 1 was non-accepted.
    if (meta.isInspectionEvent) {
      return buildInspectionRow(stage as 9 | 11 | 13, current, inspectionEvents, {
        s9Outcome,
        s9Closed,
        s11Closed,
        s13Closed,
        item,
        partStageDates,
        isProjectClosed,
      });
    }

    // Part-stage-date rows (6, 7, 8, 10, 12, 14, 15, 16, 17, 18)
    return buildPartStageRow(stage as PartStageNumber, current, partStageDates, {
      s9Outcome,
      s9Closed,
      s11Closed,
      s13Closed,
      item,
      inspectionEvents,
      hasAcceptedBg,
      isProjectClosed,
    });
  });
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

function buildBatchStageRow(
  stage: 1 | 2 | 3 | 4 | 5,
  current: number,
  partialIndicator: string | null,
  _isProjectClosed: boolean,
): StageRowState {
  const meta = STAGE_META[stage];
  let status: StageStatus;
  if (current >= stage) status = 'completed';
  else if (current === stage - 1) status = 'current';
  else status = 'locked';

  // Stage 5 surfaces the partial-batch hint when batches differ
  const lockReason =
    status === 'current' && partialIndicator
      ? partialIndicator
      : status === 'locked'
        ? `Awaiting stage ${stage - 1} on all batches`
        : null;

  return {
    stageNumber: stage,
    label: meta.label,
    cluster: meta.cluster,
    status,
    date: null, // batch-level dates come from the order-batches list, not here
    outcome: null,
    resolvedDate: null,
    // Batch dates are edited in the batch panel, not the part drilldown.
    // Mark non-editable at the drilldown level so the row is information-only.
    editable: false,
    lockReason,
    isInspectionEvent: false,
    isClusterAnchor: meta.isClusterAnchor,
  };
}

function buildSubPartRow(
  stage: StageNumber,
  current: number,
  isProjectClosed: boolean,
  partialIndicator: string | null,
): StageRowState {
  const meta = STAGE_META[stage];
  if (stage <= 5) {
    return buildBatchStageRow(
      stage as 1 | 2 | 3 | 4 | 5,
      current,
      partialIndicator,
      isProjectClosed,
    );
  }
  return {
    stageNumber: stage,
    label: meta.label,
    cluster: meta.cluster,
    status: 'not-applicable',
    date: null,
    outcome: null,
    resolvedDate: null,
    editable: false,
    lockReason: 'Part-level stages apply to PARTs only — see the parent part for stages 6-18',
    isInspectionEvent: meta.isInspectionEvent,
    isClusterAnchor: meta.isClusterAnchor,
  };
}

interface BuilderContext {
  s9Outcome: InspectionOutcome | null;
  s9Closed: boolean;
  s11Closed: boolean;
  s13Closed: boolean;
  item: PipelineStateInputs['item'];
  isProjectClosed: boolean;
}

function buildInspectionRow(
  stage: 9 | 11 | 13,
  current: number,
  events: readonly InspectionEvent[],
  ctx: BuilderContext & { partStageDates: readonly PartStageDate[] },
): StageRowState {
  const meta = STAGE_META[stage];
  const latest = getLatestInspection(events, stage);
  const closed =
    stage === 9 ? ctx.s9Closed : stage === 11 ? ctx.s11Closed : ctx.s13Closed;

  // Compose predecessor ctx — needs both stages and events
  const predCtx = { ...ctx, inspectionEvents: events };

  // Status determination
  let status: StageStatus;
  if (latest && closed) status = 'completed';
  else if (latest && !closed) status = 'current'; // recorded but unresolved
  else status = predecessorReady(stage, predCtx) ? 'current' : 'locked';

  // Editable: project open + predecessor ready + aggregate gate
  const editable =
    !ctx.isProjectClosed &&
    predecessorReady(stage, predCtx) &&
    aggregateGateReady(stage, ctx.item);

  let lockReason: string | null = null;
  if (!editable && status === 'locked') {
    lockReason = lockReasonForStage(stage, ctx);
  }
  if (latest && !closed) {
    if (stage === 13 && latest.outcome !== 'ACCEPTED' && latest.resolvedDate !== null) {
      lockReason =
        latest.eventRound >= 2
          ? `Re-functional Check outcome ${latest.outcome.toLowerCase()} — update it to Accepted to advance`
          : `Functional Check outcome ${latest.outcome.toLowerCase()} — record Re-functional Check to advance`;
    } else {
      lockReason = `Outcome ${latest.outcome.toLowerCase()} — record a resolved date to advance`;
    }
  }

  return {
    stageNumber: stage,
    label: meta.label,
    cluster: meta.cluster,
    status,
    date: latest?.eventDate ?? null,
    outcome: latest?.outcome ?? null,
    resolvedDate: latest?.resolvedDate ?? null,
    editable,
    lockReason,
    isInspectionEvent: true,
    isClusterAnchor: meta.isClusterAnchor,
  };
}

function buildPartStageRow(
  stage: PartStageNumber,
  current: number,
  partStageDates: readonly PartStageDate[],
  ctx: BuilderContext & { inspectionEvents: readonly InspectionEvent[]; hasAcceptedBg: boolean },
): StageRowState {
  const meta = STAGE_META[stage];
  const date = getStageDate(partStageDates, stage);

  // Compose the ctx the predecessor check expects (it needs both lists)
  const predCtx = { ...ctx, partStageDates };

  let status: StageStatus;
  if (date !== null) status = 'completed';
  else if (predecessorReady(stage, predCtx) && aggregateGateReady(stage, ctx.item))
    status = 'current';
  else status = 'locked';

  // Stage 16 requires an ACCEPTED BG in addition to the predecessor chain
  const blockedByBg = stage === 16 && !ctx.hasAcceptedBg;
  if (status === 'current' && blockedByBg) {
    status = 'locked';
  }

  const editable =
    !ctx.isProjectClosed &&
    status === 'current' &&
    !blockedByBg &&
    (stage !== 6 || ctx.item.isReadyForPartStages);
  // Note: stage 6 also needs isReadyForPartStages (aggregate gate). For other
  // stages, the gate state is implicit in the predecessor chain (you can't
  // get to stage 6 → 7 without 6 being recorded which already required the
  // gate to be open at the time of record).

  let lockReason: string | null = null;
  if (status === 'locked') {
    if (blockedByBg) {
      lockReason = 'Invoice locked — needs at least one Bank Guarantee with status ACCEPTED';
    } else {
      lockReason = lockReasonForStage(stage, ctx);
    }
  }

  return {
    stageNumber: stage,
    label: meta.label,
    cluster: meta.cluster,
    status,
    date,
    outcome: null,
    resolvedDate: null,
    editable,
    lockReason,
    isInspectionEvent: false,
    isClusterAnchor: meta.isClusterAnchor,
  };
}

// ---------------------------------------------------------------------------
// Predecessor + gate logic
// ---------------------------------------------------------------------------

/**
 * Mirrors the API's `checkPredecessorStageComplete` for the read side.
 * Returns true when the immediately-preceding stage (and any branch
 * dependencies) are satisfied.
 */
function predecessorReady(
  stage: 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18,
  ctx: BuilderContext & {
    partStageDates?: readonly PartStageDate[];
    inspectionEvents?: readonly InspectionEvent[];
  },
): boolean {
  const stages = ctx.partStageDates ?? [];
  const evts = ctx.inspectionEvents ?? [];
  const has = (n: PartStageNumber) => getStageDate(stages, n) !== null;
  const isClosed = (n: 9 | 11 | 13) => isInspectionClosed(evts, n);

  switch (stage) {
    case 6:
      return ctx.item.hasSubParts === true; // aggregate gate handled separately; skipped for simple parts
    case 7:
      return ctx.item.hasSubParts !== true || has(6);
    case 8:
      return has(7);
    case 9:
      return has(8);
    case 10:
      return ctx.s9Outcome !== null && ctx.s9Outcome !== 'ACCEPTED';
    case 11:
      return has(10);
    case 12:
      if (ctx.s9Outcome === null) return false;
      if (ctx.s9Outcome === 'ACCEPTED') return isClosed(9);
      return isClosed(11);
    case 13:
      return has(12);
    case 14:
      return isClosed(13);
    case 15:
      return has(14);
    case 16:
      return has(15);
    case 17:
      return has(16);
    case 18:
      return has(17);
  }
}

/**
 * Aggregate gate readiness for stage 6 specifically. Other stages inherit
 * once stage 6 is recorded.
 */
function aggregateGateReady(stage: StageNumber, item: PipelineStateInputs['item']): boolean {
  if (stage === 6) return item.hasSubParts === true && item.isReadyForPartStages;
  if (stage === 7 && item.hasSubParts !== true) return item.isReadyForPartStages;
  return true;
}

/**
 * Compose the inline lock reason for a stage when its predecessor isn't ready.
 * Kept brief — the drilldown surfaces this as a secondary line.
 */
function aggregateGateLockReason(ctx: BuilderContext): string {
  if (ctx.item.hasSubParts) {
    return 'Aggregate gate closed — every sub-part needs all active batches in-house inspected and quantity ≥ required';
  }
  if (ctx.item.activeOrderedQuantity < ctx.item.totalRequiredQuantity) {
    return `Aggregate gate closed — ordered ${ctx.item.activeOrderedQuantity} of required ${ctx.item.totalRequiredQuantity}`;
  }
  return 'Aggregate gate closed — every active batch needs in-house inspection completed';
}

function lockReasonForStage(stage: number, ctx: BuilderContext): string {
  if (stage === 6) {
    if (!ctx.item.hasSubParts) {
      return 'Skipped — parts without sub-parts do not require Assembling';
    }
    if (!ctx.item.isReadyForPartStages) {
      return aggregateGateLockReason(ctx);
    }
    return 'Stage 6 ready — record the Assembling date to begin part-level pipeline';
  }
  if (stage === 7 && !ctx.item.hasSubParts && !ctx.item.isReadyForPartStages) {
    return aggregateGateLockReason(ctx);
  }
  if (stage === 12) {
    if (ctx.s9Outcome === null) return 'Awaiting stage 9 (Inspection) to be recorded';
    if (ctx.s9Outcome === 'ACCEPTED' && !ctx.s9Closed)
      return 'Awaiting stage 9 closure';
    if (ctx.s9Outcome !== 'ACCEPTED' && !ctx.s11Closed)
      return 'Awaiting stage 11 (Re-inspection) closure';
  }
  if (stage === 14) {
    return 'Awaiting stage 13 (Functional Check / Re-functional Check) acceptance';
  }
  if (stage === 10) {
    if (ctx.s9Outcome === null) return 'Awaiting stage 9 (Inspection) to be recorded';
    return 'Auto-skipped — stage 9 outcome was ACCEPTED';
  }
  return `Awaiting stage ${stage - 1}`;
}

// ---------------------------------------------------------------------------
// Cluster grouping for rendering
// ---------------------------------------------------------------------------

export interface ClusterGroup {
  cluster: StageCluster;
  label: string;
  rows: StageRowState[];
}

const CLUSTER_LABELS: Record<StageCluster, string> = {
  PROCUREMENT: 'Procurement',
  PREPARATION: 'Preparation',
  INSPECTION: 'Inspection',
  DELIVERY: 'Delivery',
  BILLING: 'Billing',
};

export function groupByCluster(rows: readonly StageRowState[]): ClusterGroup[] {
  const map = new Map<StageCluster, StageRowState[]>();
  for (const r of rows) {
    const arr = map.get(r.cluster) ?? [];
    arr.push(r);
    map.set(r.cluster, arr);
  }
  const order: StageCluster[] = [
    'PROCUREMENT',
    'PREPARATION',
    'INSPECTION',
    'DELIVERY',
    'BILLING',
  ];
  return order.map((c) => ({ cluster: c, label: CLUSTER_LABELS[c], rows: map.get(c) ?? [] }));
}

// Silence imports kept for type re-use
void STAGE_META;
export type { BankGuarantee };
